/**
 * Geometry Object — ASAE SVG Object Wrapper for the embedded GeometryEngine.
 *
 * Synchronized Integration:
 *  - Uses SVG <foreignObject> containing a <div> as the GeometryEngine host.
 *  - Disables internal GeometryEngine requestAnimationFrame timer and internal speech TTS.
 *  - ASAE's master clock drives GeometryEngine via update(time, delta) -> geomEngine.timeline.seek(targetMs).
 *  - This guarantees 100% frame-accurate synchronization with ASAE playback, pause, seeking,
 *    scrubbing, and speech narration!
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { GeometryEngine } from '../geometry/core/GeometryEngine';
import { SpeechManager } from '../geometry/utils/SpeechManager';

export interface GeometryObjectProps {
  /**
   * Exact Teacher JSON from the geometry engine.
   * Supports both parametric and action-step formats.
   */
  teacherJSON?: object;
  /**
   * Named construction preset (kebab-case), e.g. 'angle-bisector', '90deg'.
   * Ignored if teacherJSON is set.
   */
  construction?: string;
  /** Width of the geometry canvas in ASAE SVG units (default: 820) */
  width?: number;
  /** Height of the geometry canvas in ASAE SVG units (default: 620) */
  height?: number;
  /** Animation speed multiplier (default: 1.0) */
  animationSpeed?: number;
  /** Background color of the geometry canvas (default: 'transparent') */
  backgroundColor?: string;
}

export interface GeometryObjectConfig extends SVGObjectConfig {
  props?: GeometryObjectProps;
}

/** Static counter for unique per-instance ID prefixes */
let instanceCounter = 0;

export class GeometryObject extends SVGObject {
  public props: GeometryObjectProps;
  private geomEngine: GeometryEngine | null = null;
  private hostDiv: HTMLDivElement | null = null;
  private foreignObj: SVGForeignObjectElement | null = null;
  private instanceId: string;

  constructor(config: GeometryObjectConfig) {
    super({ ...config, type: 'geometry' });
    this.props = config.props || {};
    this.instanceId = `geo-inst-${++instanceCounter}`;
  }

  public render(): void {
    // Already rendered — nothing to re-render
    if (this.foreignObj && this.geomEngine) return;

    // Disable internal speech synthesis so GeometryEngine doesn't speak over ASAE's teacher character
    SpeechManager.getInstance().setEnabled(false);

    // Clear stale children
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }

    const w = this.props.width ?? 820;
    const h = this.props.height ?? 620;

    // ── 1. <foreignObject> wraps a regular <div> ─────────────────────────────
    const NS = 'http://www.w3.org/2000/svg';
    this.foreignObj = document.createElementNS(NS, 'foreignObject') as SVGForeignObjectElement;
    this.foreignObj.setAttribute('x', String(-w / 2));
    this.foreignObj.setAttribute('y', String(-h / 2));
    this.foreignObj.setAttribute('width', String(w));
    this.foreignObj.setAttribute('height', String(h));

    this.hostDiv = document.createElement('div') as HTMLDivElement;
    this.hostDiv.style.width = '100%';
    this.hostDiv.style.height = '100%';
    this.hostDiv.style.overflow = 'hidden';
    this.hostDiv.style.position = 'relative';
    this.hostDiv.style.display = 'block';

    this.foreignObj.appendChild(this.hostDiv);
    this.container.appendChild(this.foreignObj);

    // ── 2. Boot GeometryEngine into the <div> ────────────────────────────────
    this.geomEngine = new GeometryEngine(this.hostDiv, {
      animationSpeed: this.props.animationSpeed ?? 1.0,
      backgroundColor: this.props.backgroundColor ?? 'transparent',
    });

    // Pause GeometryEngine's internal standalone RAF timer so ASAE master clock controls it
    this.geomEngine.pause();

    // ── 3. Fix global ID conflicts for svg defs ───────────────────────────────
    this._patchInstanceIds();

    // ── 4. Load content ──────────────────────────────────────────────────────
    if (this.props.teacherJSON) {
      const result = this.geomEngine.loadTeacherJSON(this.props.teacherJSON as any);
      if (!result.valid) {
        console.warn('[GeometryObject] Teacher JSON validation warnings:', result.errors);
      }
    } else if (this.props.construction) {
      try {
        this.geomEngine.constructByName(this.props.construction);
      } catch (err) {
        console.warn(`[GeometryObject] Construction "${this.props.construction}" failed:`, err);
      }
    }

    // Initial seek to time 0
    this.geomEngine.timeline.seek(0);
  }

  /**
   * Main animation tick called by ASAE's master clock on every frame.
   * Synchronizes GeometryEngine's timeline with ASAE's scene time.
   */
  public override update(time: number, delta: number): void {
    super.update(time, delta);
    if (!this.geomEngine) return;

    // Calculate local object time relative to appearTime
    const objTimeSec = Math.max(0, time - (this.appearTime || 0));
    const targetMs = objTimeSec * 1000;

    // Pure, passive seek drives geometry animation in 100% sync with ASAE master timeline
    this.geomEngine.timeline.seek(targetMs);
  }

  /**
   * Patch all svg defs IDs to be instance-unique to avoid conflicts
   * when multiple geometry objects exist in the same SVG document.
   */
  private _patchInstanceIds(): void {
    if (!this.geomEngine) return;
    const svg = this.geomEngine.renderer.svg;
    const prefix = this.instanceId;

    const knownIds = ['board-clip', 'glow-filter', 'arrow-head', 'grid-pattern'];

    knownIds.forEach(id => {
      const el = svg.querySelector(`#${id}`);
      if (el) {
        const newId = `${id}-${prefix}`;
        el.setAttribute('id', newId);

        svg.querySelectorAll(`[clip-path="url(#${id})"]`).forEach(ref => {
          ref.setAttribute('clip-path', `url(#${newId})`);
        });
        svg.querySelectorAll(`[filter="url(#${id})"]`).forEach(ref => {
          ref.setAttribute('filter', `url(#${newId})`);
        });
        svg.querySelectorAll(`[fill="url(#${id})"]`).forEach(ref => {
          ref.setAttribute('fill', `url(#${newId})`);
        });
        svg.querySelectorAll(`[marker-end="url(#${id})"]`).forEach(ref => {
          ref.setAttribute('marker-end', `url(#${newId})`);
        });
        svg.querySelectorAll(`[marker-start="url(#${id})"]`).forEach(ref => {
          ref.setAttribute('marker-start', `url(#${newId})`);
        });
      }
    });
  }

  public destroy(): void {
    if (this.geomEngine) {
      try { this.geomEngine.pause(); } catch { /* noop */ }
      this.geomEngine = null;
    }
    this.hostDiv = null;
    this.foreignObj = null;
    super.destroy();
  }

  // ── Public control API ────────────────────────────────────────────────────

  public play(): void { /* ASAE master clock controls playback */ }
  public pause(): void { /* ASAE master clock controls playback */ }
  public restart(): void { this.geomEngine?.timeline.seek(0); }
  public nextStep(): void { this.geomEngine?.nextStep(); }
  public prevStep(): void { this.geomEngine?.prevStep(); }
  public gotoStep(index: number): void { this.geomEngine?.gotoStep(index); }

  /** Load new Teacher JSON at runtime */
  public loadTeacherJSON(json: object): void {
    this.props.teacherJSON = json;
    this.props.construction = undefined;
    if (this.geomEngine) {
      this.geomEngine.loadTeacherJSON(json as any);
      this.geomEngine.timeline.seek(0);
    }
  }

  /** Load a named construction at runtime */
  public loadConstruction(name: string): void {
    this.props.construction = name;
    this.props.teacherJSON = undefined;
    if (this.geomEngine) {
      try {
        this.geomEngine.constructByName(name);
        this.geomEngine.timeline.seek(0);
      } catch (err) {
        console.warn(`[GeometryObject] loadConstruction("${name}") failed:`, err);
      }
    }
  }
}
